import { Button, Modal, Spinner } from "@heroui/react";
import { isAxiosError } from "axios";
import { Check, ExternalLink } from "lucide-react";
import { useState } from "react";

import { ImageTile, SearchBox, notify } from "@/components/kit";
import { useGetAllProducts } from "@/hooks/api/product/useGetAllProducts";
import { useAssociateScrapedProduct } from "@/hooks/api/scraped-product/useAssociateScrapedProduct";
import { useDebounce } from "@/hooks/useDebounce";
import { Product } from "@/types";
import { ScrapedProduct } from "@/types/scraped-product";

interface AssociateProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  scrapedProducts: ScrapedProduct[];
}

export const AssociateProductModal = ({
  isOpen,
  onClose,
  scrapedProducts,
}: AssociateProductModalProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null,
  );

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const {
    data: productsData,
    isPending: isLoading,
    isError,
  } = useGetAllProducts({
    searchTerm: debouncedSearchTerm,
    limit: 12,
    page: 1,
  });

  const { mutate, isPending } = useAssociateScrapedProduct();

  const products = productsData?.data || [];

  const handleClose = () => {
    onClose();
    setSelectedProductId(null);
    setSearchTerm("");
  };

  const handleConfirm = () => {
    if (!selectedProductId) return;

    mutate(
      {
        scrapedProductIds: scrapedProducts.map((p) => p.id),
        catalogProductId: selectedProductId,
      },
      {
        onSuccess: () => {
          notify.success(
            "Products Associated",
            `Successfully linked ${scrapedProducts.length} scraped product${scrapedProducts.length > 1 ? "s" : ""} to catalog product`,
          );
          handleClose();
        },
        onError: (error) => {
          let message = "Please try again and contact support";

          if (isAxiosError(error)) {
            message = error.response?.data?.message || message;
          }

          notify.error("Association Failed", message);
        },
      },
    );
  };

  return (
    <Modal.Backdrop
      isOpen={isOpen}
      variant="opaque"
      onOpenChange={(open) => !open && handleClose()}
    >
      <Modal.Container placement="center" scroll="inside">
        <Modal.Dialog className="w-full max-w-xl">
          <Modal.CloseTrigger />
          <Modal.Header>
            <Modal.Heading>Link to catalog product</Modal.Heading>
          </Modal.Header>
          <Modal.Body className="flex flex-col gap-5 p-1">
            <section className="flex flex-col gap-2">
              <div className="text-muted flex items-center justify-between text-xs">
                <span>
                  Scraped product{scrapedProducts.length > 1 ? "s" : ""}
                </span>
                <span className="tabular-nums">
                  {scrapedProducts.length} selected
                </span>
              </div>
              <ul className="flex max-h-44 flex-col gap-2 overflow-y-auto">
                {scrapedProducts.map((product) => (
                  <li key={product.id} className="flex items-center gap-3">
                    <ImageTile src={product.images?.[0]} />
                    <div className="flex min-w-0 flex-1 flex-col leading-tight">
                      <span className="truncate font-medium">
                        {product.name}
                      </span>
                      <span className="text-muted text-xs">
                        {product.supplier.name} ·{" "}
                        {product.stockAvailability
                          ? "In stock"
                          : "Out of stock"}
                      </span>
                    </div>
                    {product.externalUrl ? (
                      <a
                        className="hidden items-center gap-1 text-sm hover:underline sm:inline-flex"
                        href={product.externalUrl}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        View source
                        <ExternalLink className="size-3.5" />
                      </a>
                    ) : null}
                    <span className="font-medium whitespace-nowrap tabular-nums">
                      AED {product.price.toFixed(2)}
                    </span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="flex flex-col gap-2">
              <span className="text-muted text-xs">Catalog product</span>
              <SearchBox
                className="w-full max-w-none"
                placeholder="Search catalog products"
                value={searchTerm}
                onChange={setSearchTerm}
              />
              <div className="min-h-48">
                {isLoading ? (
                  <div className="flex h-48 flex-col items-center justify-center gap-2">
                    <Spinner />
                    <p className="text-muted text-sm">Searching...</p>
                  </div>
                ) : isError ? (
                  <p className="text-muted py-12 text-center text-sm">
                    Failed to load products
                  </p>
                ) : products.length === 0 ? (
                  <p className="text-muted py-12 text-center text-sm">
                    {searchTerm
                      ? "No products found"
                      : "Type to search products"}
                  </p>
                ) : (
                  <div
                    aria-label="Catalog products"
                    className="-mx-1 flex max-h-72 flex-col overflow-y-auto"
                    role="radiogroup"
                  >
                    {products.map((product) => (
                      <CatalogProductRow
                        key={product.id}
                        isSelected={selectedProductId === product.id}
                        product={product}
                        onSelect={() => setSelectedProductId(product.id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </section>
          </Modal.Body>
          <Modal.Footer>
            <Button
              isDisabled={isPending}
              variant="ghost"
              onPress={handleClose}
            >
              Cancel
            </Button>
            <Button
              isDisabled={!selectedProductId}
              isPending={isPending}
              onPress={handleConfirm}
            >
              Link product
            </Button>
          </Modal.Footer>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
};

const CatalogProductRow = ({
  product,
  isSelected,
  onSelect,
}: {
  product: Product;
  isSelected: boolean;
  onSelect: () => void;
}) => (
  <button
    aria-checked={isSelected}
    className={`flex w-full cursor-(--cursor-interactive) items-center gap-3 rounded-2xl px-3 py-2 text-start outline-none focus-visible:ring-2 focus-visible:ring-(--focus) ${
      isSelected ? "bg-surface-secondary" : "hover:bg-surface-secondary/60"
    }`}
    role="radio"
    type="button"
    onClick={onSelect}
  >
    <span
      aria-hidden="true"
      className={`flex size-4 shrink-0 items-center justify-center rounded-full border ${
        isSelected
          ? "border-(--accent) bg-(--accent) text-(--accent-foreground)"
          : "border-(--border)"
      }`}
    >
      {isSelected ? <Check className="size-3" /> : null}
    </span>
    <ImageTile src={product.images?.[0]} />
    <span className="flex min-w-0 flex-1 flex-col leading-tight">
      <span className="truncate font-medium">{product.name}</span>
      {product.inStock ? (
        <span className="text-muted text-xs">In stock</span>
      ) : (
        <span className="text-xs text-(--danger)">Out of stock</span>
      )}
    </span>
    <span className="font-medium whitespace-nowrap tabular-nums">
      AED {Number(product.price).toFixed(2)}
    </span>
  </button>
);
